/**
 * Header mega menu controller.
 * Spec: docs/superpowers/specs/2026-09-22-header-mega-menu-design.md (§A2, §A4, §A5)
 *
 * Plain browser script, loaded with `defer` by sections/header.liquid. The pure
 * helpers at the top are also exported for Node unit tests
 * (tests/unit/header-menu.test.cjs); when Node loads this file it returns right
 * after the export, before touching the DOM.
 *
 * Sets on document.documentElement:
 *   --header-bottom  px bottom edge of the fixed header. Used by the hamburger
 *                    panel, the mega panel's max-height and the collection
 *                    filters' sticky sidebar (filters plan).
 *   --sd-scrollbar-w px width of a classic vertical scrollbar (0 for overlay
 *                    scrollbars); keeps the panel's 20px right margin visible.
 */
(function () {
  'use strict';

  var NAV_GAP = 16;
  var CLOSE_DELAY = 200;

  /**
   * Whether the desktop nav fits beside the centred logo (spec §A4, D8): its
   * natural width + 16px must end at or before the logo's left edge. The grid
   * cell isn't the test; the nav may overflow its cell as long as it clears the logo.
   * @param {{navWidth: number, logoLeft: number, contentLeft: number, gap?: number}} m
   *   navWidth: the nav list's natural width; logoLeft: the logo's left edge;
   *   contentLeft: the header's left edge plus its left padding.
   * @returns {boolean}
   */
  function navFits(m) {
    var gap = m.gap == null ? NAV_GAP : m.gap;
    return m.navWidth + gap <= m.logoLeft - m.contentLeft;
  }

  /**
   * Width of a classic vertical scrollbar.
   * @param {number} innerWidth - window.innerWidth
   * @param {number} clientWidth - document.documentElement.clientWidth
   * @returns {number}
   */
  function scrollbarWidth(innerWidth, clientWidth) {
    return Math.max(0, Math.round(innerWidth - clientWidth));
  }

  /**
   * What a click on a dropdown trigger does (spec §A4 "Click or tap toggles").
   * On hover devices, a mouse click on a panel that hover just opened keeps it
   * open instead of closing it under the pointer; the next click closes it.
   * Keyboard clicks (Enter/Space, detail 0) and taps always toggle.
   * @param {{isOpen: boolean, openedBy: (string|null), canHover: boolean, detail: number}} s
   * @returns {'open'|'close'|'keep'}
   */
  function clickAction(s) {
    if (!s.isOpen) return 'open';
    if (s.canHover && s.detail > 0 && s.openedBy === 'hover') return 'keep';
    return 'close';
  }

  /**
   * Hover intent: open on enter, close `delay` ms after leave, cancelled by re-entering.
   * @param {{onOpen: function(): void, onClose: function(): void, delay?: number,
   *          setTimeout?: function, clearTimeout?: function}} opts
   * @returns {{enter: function(): void, leave: function(): void, cancel: function(): void, isPending: function(): boolean}}
   */
  function createHoverIntent(opts) {
    var delay = opts.delay == null ? CLOSE_DELAY : opts.delay;
    var setT = opts.setTimeout || setTimeout;
    var clearT = opts.clearTimeout || clearTimeout;
    var timer = null;

    function cancel() {
      if (timer !== null) {
        clearT(timer);
        timer = null;
      }
    }

    return {
      enter: function () {
        cancel();
        opts.onOpen();
      },
      leave: function () {
        cancel();
        timer = setT(function () {
          timer = null;
          opts.onClose();
        }, delay);
      },
      cancel: cancel,
      isPending: function () {
        return timer !== null;
      },
    };
  }

  if (typeof module !== 'undefined' && module.exports) module.exports = { navFits: navFits, scrollbarWidth: scrollbarWidth, clickAction: clickAction, createHoverIntent: createHoverIntent };
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  // Double-run guard (spec Important 2): the theme editor re-executes <script> tags in a
  // re-rendered section, and doesn't guarantee this second copy runs before or after the
  // fresh header dispatches shopify:section:load. If this copy ran initHeader() itself
  // (below, unguarded, without rebind:true) it would set header.__sdMenu without binding
  // the hamburger, and the real shopify:section:load listener's rebind pass would then
  // return early at initHeader's own "if (header.__sdMenu) return" guard, leaving the
  // hamburger dead. Bailing out here, before that call, keeps both orders safe.
  if (window.SDHeaderMenu) return;

  var CART_SELECTORS = ['[data-dropdown="cart-dropdown"]', '[data-dropdown="mobile-cart-dropdown"]'];
  var root = document.documentElement;
  var canHoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');
  var desktopQuery = window.matchMedia('(min-width: 768px)');

  function measure(header) {
    var list = header.querySelector('[data-header-nav-list]');
    var logo = header.querySelector('[data-header-logo]');
    if (!list || !logo) return null;
    var pad = parseFloat(getComputedStyle(header).paddingLeft) || 0;
    return {
      navWidth: list.getBoundingClientRect().width,
      logoLeft: logo.getBoundingClientRect().left,
      contentLeft: header.getBoundingClientRect().left + pad,
    };
  }

  /** True when something inside `el` has keyboard (focus-visible) focus. */
  function hasKeyboardFocus(el) {
    try {
      return !!el.querySelector(':focus-visible');
    } catch (error) {
      return false;
    }
  }

  /**
   * @param {HTMLElement} header
   * @param {{rebind?: boolean}} [opts] - rebind: this header replaced one after a theme-editor re-render
   */
  function initHeader(header, opts) {
    if (header.__sdMenu) return header.__sdMenu;
    opts = opts || {};

    var cleanups = [];
    var destroyed = false;
    var items = Array.prototype.slice.call(header.querySelectorAll('[data-mega-item]'));
    var openItem = null;
    var openedBy = null;

    function on(target, type, fn, options) {
      target.addEventListener(type, fn, options);
      cleanups.push(function () {
        target.removeEventListener(type, fn, options);
      });
    }

    function triggerOf(item) {
      return item.querySelector('[data-mega-trigger]');
    }

    // ---- Geometry: --header-bottom and --sd-scrollbar-w ----
    // Scroll fires this every frame (the ticker's `top 0.3s` transition can move the
    // header while scrolling), so skip a write whenever the value hasn't actually
    // changed (Minor 2).
    var lastBottom = null;
    var lastScrollbarW = null;
    function updateGeometry() {
      var bottom = Math.round(header.getBoundingClientRect().bottom);
      if (bottom !== lastBottom) {
        lastBottom = bottom;
        root.style.setProperty('--header-bottom', bottom + 'px');
      }
      var scrollbarW = scrollbarWidth(window.innerWidth, root.clientWidth);
      if (scrollbarW !== lastScrollbarW) {
        lastScrollbarW = scrollbarW;
        root.style.setProperty('--sd-scrollbar-w', scrollbarW + 'px');
      }
    }

    // ---- Cart interplay (spec §A4), menu -> cart ----
    function closeCarts() {
      CART_SELECTORS.forEach(function (selector) {
        var dropdown = document.querySelector(selector);
        if (!dropdown) return;
        var wasOpen = dropdown.getAttribute('data-open') === 'true';
        dropdown.setAttribute('data-open', 'false');
        var trigger = dropdown.querySelector('.dropdown-trigger');
        if (trigger) trigger.setAttribute('aria-expanded', 'false');
        if (wasOpen && selector.indexOf('mobile') !== -1) {
          header.classList.remove('mobile-dropdown-open');
          document.body.style.overflow = '';
        }
      });
    }

    // ---- Hamburger (legacy [data-dropdown] markup, spec §A5) ----
    var hamburger = header.querySelector('[data-dropdown="mobile-shop-dropdown"]');
    var hamburgerTrigger = header.querySelector('#mobile-shop-dropdown-trigger');

    /** Same state changes as the section's legacy toggle, without depending on its handler. */
    function setHamburger(isOpen) {
      if (!hamburger) return;
      hamburger.setAttribute('data-open', isOpen ? 'true' : 'false');
      if (hamburgerTrigger) hamburgerTrigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      header.classList.toggle('mobile-dropdown-open', isOpen);
      document.body.style.overflow = isOpen ? 'hidden' : '';
    }

    function openHamburger() {
      closeCarts();
      setHamburger(true);
    }

    // The section's {% javascript %} binds the hamburger once, at DOMContentLoaded.
    // After a theme-editor re-render the new button has no handler, so bind ours.
    // Never on first load: next to the legacy handler it would toggle twice.
    if (opts.rebind && hamburgerTrigger) {
      on(hamburgerTrigger, 'click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        if (hamburgerTrigger.getAttribute('aria-expanded') === 'true') setHamburger(false);
        else openHamburger();
      });
    }

    // ---- Open / close: one panel at a time ----
    function open(item, how) {
      if (openItem === item) return;
      if (openItem) close(openItem, false);
      closeCarts();
      // A panel always opens with fresh --header-bottom/--sd-scrollbar-w (Minor 2),
      // rather than whatever the last scroll/resize-queued rAF happened to leave.
      updateGeometry();
      openItem = item;
      openedBy = how;
      item.classList.add('is-open');
      triggerOf(item).setAttribute('aria-expanded', 'true');
    }

    function close(item, restoreFocus) {
      if (!item || openItem !== item) return;
      item.classList.remove('is-open');
      var trigger = triggerOf(item);
      trigger.setAttribute('aria-expanded', 'false');
      openItem = null;
      openedBy = null;
      if (restoreFocus) trigger.focus();
    }

    function closeAll() {
      if (openItem) close(openItem, false);
    }

    items.forEach(function (item) {
      var trigger = triggerOf(item);
      var intent = createHoverIntent({
        onOpen: function () {
          open(item, 'hover');
        },
        onClose: function () {
          // A panel holding keyboard focus stays open (Review Focus 3).
          if (hasKeyboardFocus(item)) return;
          close(item, false);
        },
      });
      cleanups.push(intent.cancel);

      on(item, 'pointerenter', function (event) {
        if (event.pointerType === 'touch' || !canHoverQuery.matches) return;
        intent.enter();
      });
      on(item, 'pointerleave', function (event) {
        if (event.pointerType === 'touch' || !canHoverQuery.matches) return;
        if (openItem === item) intent.leave();
      });
      on(trigger, 'click', function (event) {
        event.preventDefault();
        var action = clickAction({
          isOpen: openItem === item,
          openedBy: openedBy,
          canHover: canHoverQuery.matches,
          detail: event.detail,
        });
        if (action === 'open') open(item, event.detail === 0 ? 'keyboard' : 'click');
        else if (action === 'keep') openedBy = 'click';
        else close(item, false);
      });
      on(item, 'focusout', function (event) {
        // relatedTarget is null when focus leaves the window or lands on a
        // non-focusable area; the outside pointerdown handler covers pointers.
        if (event.relatedTarget && !item.contains(event.relatedTarget)) close(item, false);
      });
    });

    on(document, 'keydown', function (event) {
      if (event.key !== 'Escape' || !openItem) return;
      close(openItem, openItem.contains(document.activeElement));
    });

    // pointerdown, not click: iOS Safari doesn't deliver taps on non-clickable
    // elements to a document click listener, so an iPad (expanded header, no
    // hover) could never tap outside to close (Review Focus 2).
    on(document, 'pointerdown', function (event) {
      if (openItem && !openItem.contains(event.target)) close(openItem, false);
    });

    // ---- Cart interplay (spec §A4), cart -> menu: covers hover, click and openCartDropdown() ----
    var cartObserver = new MutationObserver(function (records) {
      var opened = false;
      var mobileCartOpened = false;
      for (var i = 0; i < records.length; i++) {
        var target = records[i].target;
        if (target.getAttribute('data-open') !== 'true') continue;
        opened = true;
        if (target.getAttribute('data-dropdown') === 'mobile-cart-dropdown') mobileCartOpened = true;
      }
      if (!opened) return;
      closeAll();
      // The legacy dropdown click handler (sections/header.liquid) and
      // openCartDropdown() force every OTHER dropdown's data-open to false when
      // one opens, including the hamburger's -- but only their mouseenter path
      // also clears body.style.overflow and .mobile-dropdown-open. A keyboard
      // Enter (or any non-hover open) on the desktop cart can otherwise leave
      // the page scroll-locked and the header dark (fix round 1, I2).
      // Never here when the mobile cart itself just opened: at <768px it
      // legitimately owns that same lock (its own click handler set it).
      if (!mobileCartOpened && header.classList.contains('mobile-dropdown-open') && (!hamburger || hamburger.getAttribute('data-open') !== 'true')) {
        setHamburger(false);
      }
    });
    CART_SELECTORS.forEach(function (selector) {
      var dropdown = document.querySelector(selector);
      if (dropdown) cartObserver.observe(dropdown, { attributes: true, attributeFilter: ['data-open'] });
    });
    cleanups.push(function () {
      cartObserver.disconnect();
    });

    // ---- Collapse (spec §A4, D8). While collapsed the nav is visibility:hidden +
    // position:absolute (CSS), so it stays measurable at its natural width. ----
    function closeHamburger() {
      // Guarded: setHamburger(false) clears body overflow, which an open mobile cart still needs.
      if (hamburger && hamburger.getAttribute('data-open') === 'true') setHamburger(false);
    }

    function applyCollapse() {
      var m = measure(header);
      var collapsed = !desktopQuery.matches || !m || !navFits(m);
      var wasCollapsed = header.classList.contains('header--collapsed');
      header.classList.toggle('header--collapsed', collapsed);
      if (collapsed && !wasCollapsed) closeAll();
      if (!collapsed && wasCollapsed) closeHamburger();
      // At 768px+ the mobile cart's trigger is display:none. A cart opened on a
      // narrower screen (a phone rotated to landscape) would otherwise stay open
      // invisibly, with the page scroll-locked and the header dark.
      if (desktopQuery.matches) {
        var mobileCart = document.querySelector('[data-dropdown="mobile-cart-dropdown"]');
        if (mobileCart && mobileCart.getAttribute('data-open') === 'true') closeCarts();
      }
      updateGeometry();
    }

    var measureQueued = false;
    function queueMeasure() {
      if (measureQueued || destroyed) return;
      measureQueued = true;
      requestAnimationFrame(function () {
        measureQueued = false;
        if (!destroyed) applyCollapse();
      });
    }

    var resizeObserver = new ResizeObserver(queueMeasure);
    [header, header.querySelector('[data-header-nav-list]'), header.querySelector('[data-header-logo]')].forEach(function (el) {
      if (el) resizeObserver.observe(el);
    });
    cleanups.push(function () {
      resizeObserver.disconnect();
    });
    on(desktopQuery, 'change', queueMeasure);
    // OffBit is font-display: swap, so the nav's width changes once it loads.
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(queueMeasure);

    // Back/forward cache: a restored page must not come back with a panel, a
    // cart or the hamburger open and scrolling locked (Review Focus 1).
    on(window, 'pageshow', function (event) {
      if (!event.persisted) return;
      closeAll();
      closeCarts();
      closeHamburger();
      queueMeasure();
    });

    // ---- --header-bottom tracking: scroll, resize, ticker class changes, top transition ----
    var geometryQueued = false;
    function queueGeometry() {
      if (geometryQueued || destroyed) return;
      geometryQueued = true;
      requestAnimationFrame(function () {
        geometryQueued = false;
        if (!destroyed) updateGeometry();
      });
    }
    on(window, 'scroll', queueGeometry, { passive: true });
    on(window, 'resize', queueGeometry);

    // The ticker moves the header with `transition: top 0.3s`; follow it frame by frame.
    var loopId = 0;
    function loop() {
      updateGeometry();
      loopId = requestAnimationFrame(loop);
    }
    function stopLoop(event) {
      if (event && event.target !== header) return;
      if (loopId) cancelAnimationFrame(loopId);
      loopId = 0;
      updateGeometry();
    }
    on(header, 'transitionrun', function (event) {
      if (event.target === header && !loopId) loopId = requestAnimationFrame(loop);
    });
    on(header, 'transitionend', stopLoop);
    on(header, 'transitioncancel', stopLoop);
    cleanups.push(function () {
      if (loopId) cancelAnimationFrame(loopId);
    });

    var classObserver = new MutationObserver(queueGeometry);
    classObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
    classObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    cleanups.push(function () {
      classObserver.disconnect();
    });

    // ---- Theme editor: show a Dropdown cards block's panel (spec §A4) ----
    function showBlock(blockId, show) {
      if (!header.classList.contains('header--collapsed')) {
        var panel = header.querySelector('[data-mega-panel][data-cards-block="' + blockId + '"]');
        var item = panel && panel.closest('[data-mega-item]');
        if (!item) return;
        if (show) open(item, 'editor');
        else close(item, false);
        return;
      }
      var details = header.querySelector('[data-mobile-item][data-cards-block="' + blockId + '"]');
      if (!details) return;
      if (show) openHamburger();
      details.open = show;
    }

    var api = {
      showBlock: showBlock,
      closeAll: closeAll,
      destroy: function () {
        destroyed = true;
        closeAll();
        cleanups.forEach(function (fn) {
          fn();
        });
        cleanups = [];
        delete header.__sdMenu;
        header.removeAttribute('data-menu-ready');
      },
    };
    header.__sdMenu = api;

    applyCollapse();
    header.setAttribute('data-menu-ready', 'true');
    return api;
  }

  function headerIn(scope) {
    if (!scope) return null;
    if (scope.matches && scope.matches('[data-header]')) return scope;
    return scope.querySelector ? scope.querySelector('[data-header]') : null;
  }

  // Theme editor re-renders (spec §A4): re-initialise the new markup, including
  // the hamburger toggle that the section's legacy script no longer binds.
  document.addEventListener('shopify:section:load', function (event) {
    var header = headerIn(event.target);
    if (header) initHeader(header, { rebind: true });
  });

  document.addEventListener('shopify:section:unload', function (event) {
    var header = headerIn(event.target);
    if (header && header.__sdMenu) header.__sdMenu.destroy();
  });

  function onBlockEvent(show) {
    return function (event) {
      var header = document.querySelector('[data-header]');
      var blockId = event.detail && event.detail.blockId;
      if (header && header.__sdMenu && blockId) header.__sdMenu.showBlock(blockId, show);
    };
  }
  document.addEventListener('shopify:block:select', onBlockEvent(true));
  document.addEventListener('shopify:block:deselect', onBlockEvent(false));

  var firstHeader = document.querySelector('[data-header]');
  if (firstHeader) initHeader(firstHeader);

  window.SDHeaderMenu = { init: initHeader, navFits: navFits };
})();
